import { StandardizedConnection } from '@/types/connections';

/** Raw Plaid status string from org storage or exchange-token payload. */
export function getPlaidRawStatus(connector: StandardizedConnection): string | undefined {
  const original = connector.metadata?.originalItem;
  if (original && typeof original.status === 'string') {
    return original.status;
  }
  const status = connector.status as string;
  if (typeof status === 'string' && ['expiring', 'active', 'disconnected'].includes(status)) {
    return status;
  }
  return undefined;
}

/** Server-provided flag (access_token is never sent to the client). */
function getPlaidHasAccessToken(connector: StandardizedConnection): boolean | undefined {
  if (typeof connector.metadata?.has_access_token === 'boolean') {
    return connector.metadata.has_access_token;
  }
  if (typeof connector.metadata?.originalItem?.metadata?.has_access_token === 'boolean') {
    return connector.metadata.originalItem.metadata.has_access_token as boolean;
  }
  return undefined;
}

function getPlaidSetupState(connector: StandardizedConnection): string | undefined {
  if (typeof connector.setup_state === 'string') return connector.setup_state;
  const statusMeta = connector.metadata?.status;
  if (statusMeta && typeof statusMeta === 'object' && 'setup_state' in statusMeta) {
    return statusMeta.setup_state as string;
  }
  const originalStatus = connector.metadata?.originalItem?.status;
  if (originalStatus && typeof originalStatus === 'object' && 'setup_state' in originalStatus) {
    return originalStatus.setup_state as string;
  }
  return undefined;
}

/** True when Figabl no longer has a Plaid access token for this connection. */
export function plaidConnectionDataLost(connector: StandardizedConnection): boolean {
  if (connector.service !== 'plaid' && connector.type !== 'plaid') return false;
  if (connector.metadata?.connection_data_lost === true) return true;
  return getPlaidHasAccessToken(connector) === false;
}

/** Whether a Plaid bank connection needs re-authentication (mirrors OrganisationOverview). */
export function plaidConnectionNeedsReauth(connector: StandardizedConnection): boolean {
  if (connector.service !== 'plaid') return false;

  if (connector.needs_reconnect || connector.metadata?.needs_reconnect) {
    return true;
  }

  const hasAccessToken = getPlaidHasAccessToken(connector);
  if (hasAccessToken === false) {
    return true;
  }

  const setupState = getPlaidSetupState(connector);
  if (setupState === 'needs_reauth' || setupState === 'broken') {
    return true;
  }
  if (setupState === 'connected' && hasAccessToken !== false) {
    return false;
  }

  const raw = getPlaidRawStatus(connector);
  if (raw === 'expiring' || raw === 'error' || raw === 'disconnected') {
    return true;
  }
  if (raw === 'active' || raw === 'connected') {
    return false;
  }

  return false;
}
